/**
 * Data layer for the mobile attribution vendor postback template feature.
 * Thin typed fetch wrappers over /api/v1/adjust-postback-template (slug fixed by
 * the API registry; UI copy stays vendor-neutral).
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface MmpPostbackTemplateRow {
	id: string;
	tenant_id: string;
	template_key: string;
	name: string;
	url_template: string;
	event_token: string | null;
	active: number;
	created_at: number;
	updated_at: number;
}

export interface ListMmpPostbackTemplateResponse {
	items: MmpPostbackTemplateRow[];
}

export interface CreateMmpPostbackTemplateBody {
	template_key: string;
	name: string;
	url_template: string;
	event_token?: string | null;
	active?: boolean;
}

export interface CreateMmpPostbackTemplateResponse {
	ok: true;
	id: string;
	template_key: string;
}

const BASE = "/api/v1/adjust-postback-template";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListMmpPostbackTemplateResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListMmpPostbackTemplateResponse>(res);
}

export async function create(
	body: CreateMmpPostbackTemplateBody,
): Promise<CreateMmpPostbackTemplateResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateMmpPostbackTemplateResponse>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const mmpPostbackTemplateQueryKeys = {
	all: ["adjust-postback-template"] as const,
	list: () => [...mmpPostbackTemplateQueryKeys.all] as const,
	detail: (id: string) => [...mmpPostbackTemplateQueryKeys.all, id] as const,
};

/**
 * Macro substitution utilities — render a URL template by replacing
 * {macro_name} placeholders with values from a context map.
 * Unknown macros are left as-is so users can spot typos in preview.
 * This is the data-layer pure function exercised by vitest.
 */
export interface MacroContext {
	[key: string]: string | number | null | undefined;
}

export const KNOWN_MACROS: readonly string[] = [
	"click_id",
	"gps_adid",
	"idfa",
	"event_token",
	"campaign_id",
	"publisher_id",
	"install_ts",
	"event_ts",
] as const;

const MACRO_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

export function renderTemplate(template: string, ctx: MacroContext): string {
	if (typeof template !== "string") return "";
	return template.replace(MACRO_RE, (match, key: string) => {
		const v = ctx[key];
		if (v === undefined || v === null) return match;
		return encodeURIComponent(String(v));
	});
}

export function extractMacros(template: string): string[] {
	if (typeof template !== "string") return [];
	const out: string[] = [];
	const seen = new Set<string>();
	MACRO_RE.lastIndex = 0;
	let m: RegExpExecArray | null = MACRO_RE.exec(template);
	while (m !== null) {
		const k = m[1];
		if (k && !seen.has(k)) {
			seen.add(k);
			out.push(k);
		}
		m = MACRO_RE.exec(template);
	}
	return out;
}
